"use client";

import { Fragment, useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";
import type { AgentMessage, ExtensionUiRequest, SessionInfo, SessionTreeNode } from "@/lib/types";
import { MessageView } from "./MessageView";
import { ChatInput, type AttachedImage, type ChatInputHandle } from "./ChatInput";
import { ChatMinimap, useMessageRefs } from "./ChatMinimap";
import { useAgentSession, type AgentPhase, type NoticeItem } from "@/hooks/useAgentSession";
import { useDragDrop } from "@/hooks/useDragDrop";
import { useIsMobile } from "@/hooks/useIsMobile";
import type { SessionStatsInfo } from "@/lib/pi-types";
import { getHiddenSkillToolCallIds, redactSkillText } from "@/lib/skill-redaction";

interface Props {
  session: SessionInfo | null;
  newSessionCwd: string | null;
  onAgentEnd?: () => void;
  onSessionCreated?: (session: SessionInfo) => void;
  onSessionForked?: (newSessionId: string) => void;
  onQuotaChanged?: () => void;
  modelsRefreshKey?: number;
  chatInputRef?: React.RefObject<ChatInputHandle | null>;
  onBranchDataChange?: (tree: SessionTreeNode[], activeLeafId: string | null, onLeafChange: (leafId: string | null) => void) => void;
  onSystemPromptChange?: (prompt: string | null) => void;
  onSessionStatsChange?: (stats: SessionStatsInfo | null) => void;
  onSessionStatsPanelOpen?: () => void;
  onContextUsageChange?: (usage: { percent: number | null; contextWindow: number; tokens: number | null } | null) => void;
  onOpenMembership?: () => void;
  remainingChats?: number;
  autoStartPrompt?: { id: string; prompt: string } | null;
  onAutoStartPromptConsumed?: (id: string) => void;
}

function phaseLabel(phase: AgentPhase): string {
  if (phase?.kind === "running_tools") {
    const names = phase.tools.map((t) => t.name);
    if (names.length === 0) return "正在运行工具...";
    if (names.length === 1) return `正在运行 ${names[0]}...`;
    if (names.length <= 3) return `正在运行 ${names.join(", ")}...`;
    return `正在运行 ${names.slice(0, 2).join(", ")}（另 ${names.length - 2} 个）...`;
  }
  if (phase?.kind === "waiting_model") return "等待模型响应...";
  return "思考中...";
}

const TYPEWRITER_PHRASES = [
  "看懂女生消息背后的意思。",
  "判断她是在试探、分享还是拒绝。",
  "帮你想清楚怎么回最合适。",
  "陪你把长期关系经营到稳定。",
  "帮你处理冷淡、冷战和争吵。",
  "帮你推进暧昧、恋爱到谈婚论嫁。",
  "帮你把话说得真诚、有边界。",
  "帮你判断这段关系下一步怎么走。",
];

const GUIDE_STEPS = [
  { title: "发来她的消息", desc: "文字、聊天截图、朋友圈内容都可以，先看她真正想表达什么。" },
  { title: "判断怎么回复", desc: "根据关系阶段、情绪信号和窗口强弱，给你更合适的回法。" },
  { title: "经营长期关系", desc: "处理冷淡、争吵、边界、安全感和推进节奏，目标是稳定走下去。" },
];

const GUIDE_ACTIONS = [
  { title: "她什么意思", prompt: "帮我分析女生这句话想表达什么，我应该怎么理解。我会把她的原话发给你。" },
  { title: "怎么回复她", prompt: "帮我判断这条消息怎么回更合适。我会发女生原话和我们现在的关系阶段。" },
  { title: "判断关系阶段", prompt: "帮我判断我们现在处在什么关系阶段，下一步最该做什么。我会补充我们的相处情况。" },
  { title: "争吵冷战修复", prompt: "我们刚吵完，现在有点冷战。帮我复盘问题、判断责任边界，并给我今天该怎么修复。" },
  { title: "表达需求", prompt: "我想表达自己的需求，但不想让她觉得我在指责。帮我组织一段真诚、有边界的话。" },
  { title: "谈婚论嫁规划", prompt: "我们开始考虑谈婚论嫁了，帮我梳理需要确认的关键问题：家庭、金钱、城市、孩子、边界和时间表。" },
  { title: "长期关系档案", prompt: "帮我初始化长期关系档案。请优先建立我的用户个人档案，记录我的基本情况、恋爱目标、沟通风格、关系需求、底线和当前困扰；再根据需要补充对方和关系信息。" },
];

const MATERIAL_HINTS = [
  "她的原话或聊天截图",
  "你们现在的关系阶段",
  "上一轮你怎么回的",
  "你想达到的结果",
];

const SCENARIO_PROMPTS = [
  { title: "朋友圈怎么发", prompt: "帮我设计一条适合发朋友圈的内容，目标是自然展示状态、不过度暴露需求感，并让女生有机会接话。我会补充我的近况和想传达的感觉。" },
  { title: "约会后怎么推进", prompt: "我们刚约会完，帮我判断这次约会反馈如何，以及接下来 24 小时内怎么发消息更自然。" },
  { title: "边界和需求表达", prompt: "我想表达自己的需求和边界，但不想显得指责或控制。帮我组织一段成熟、真诚、对方能听进去的话。" },
  { title: "谈婚论嫁问题", prompt: "我们已经到了谈婚论嫁阶段，帮我梳理需要沟通的关键问题，并设计一次不压迫对方的沟通方式。" },
];

const CHAT_MINIMAP_WIDTH = 36;
const CHAT_COLUMN_PADDING = 16;
const CHAT_INPUT_RIGHT_PADDING = CHAT_COLUMN_PADDING + CHAT_MINIMAP_WIDTH;

function Typewriter({ phrases }: { phrases: string[] }) {
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * phrases.length));
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [caretOn, setCaretOn] = useState(true);
  const currentPhrases = phrases.length > 0 ? phrases : TYPEWRITER_PHRASES;

  useEffect(() => {
    const blink = setInterval(() => setCaretOn((v) => !v), 530);
    return () => clearInterval(blink);
  }, []);

  useEffect(() => {
    setPhraseIdx((idx) => idx >= currentPhrases.length ? 0 : idx);
  }, [currentPhrases.length]);

  useEffect(() => {
    const current = currentPhrases[phraseIdx] ?? currentPhrases[0];
    let timeout: ReturnType<typeof setTimeout>;
    if (!deleting && text === current) {
      timeout = setTimeout(() => setDeleting(true), 1800);
    } else if (deleting && text === "") {
      setDeleting(false);
      setPhraseIdx((i) => (i + 1) % currentPhrases.length);
    } else {
      const next = deleting ? current.slice(0, text.length - 1) : current.slice(0, text.length + 1);
      timeout = setTimeout(() => setText(next), deleting ? 28 : 55);
    }
    return () => clearTimeout(timeout);
  }, [text, deleting, phraseIdx, currentPhrases]);

  return (
    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
      {text}
      <span style={{ opacity: caretOn ? 1 : 0, color: "var(--accent)", marginLeft: 1 }}>▍</span>
    </span>
  );
}

function userMessageKey(message: AgentMessage): string | null {
  if (message.role !== "user") return null;
  const content = message.content;
  if (typeof content === "string") return redactSkillText(content).trim();
  if (!Array.isArray(content)) return String(content);
  return content
    .map((block) => {
      if (block.type === "text") return redactSkillText(block.text);
      if (block.type === "image") return "[image]";
      return JSON.stringify(block);
    })
    .join("\n")
    .trim();
}

function assistantMessageKey(message: AgentMessage | Partial<AgentMessage> | undefined): string | null {
  if (!message || message.role !== "assistant" || !Array.isArray(message.content)) return null;
  const text = message.content
    .filter((block) => block.type === "text")
    .map((block) => redactSkillText(block.text))
    .join("\n")
    .trim();
  if (!text) return null;
  return text.replace(/\s+/g, " ").slice(0, 320);
}

function ExtensionWidgets({ widgets }: { widgets: Array<{ key: string; lines: string[] }> }) {
  if (widgets.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
      {widgets.map((widget) => (
        <div
          key={widget.key}
          style={{
            border: "1px solid var(--border)",
            borderRadius: 7,
            background: "var(--bg-panel)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "5px 9px", borderBottom: "1px solid var(--border)", color: "var(--text-dim)", fontSize: 11, fontFamily: "var(--font-mono)" }}>
            {widget.key}
          </div>
          <pre style={{ margin: 0, padding: "8px 9px", color: "var(--text-muted)", fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "var(--font-mono)" }}>
            {widget.lines.join("\n")}
          </pre>
        </div>
      ))}
    </div>
  );
}

function NoticeShelf({ notices, floating = false, align = "left" }: { notices: NoticeItem[]; floating?: boolean; align?: "left" | "right" }) {
  if (notices.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "right" ? "flex-end" : "stretch",
        marginBottom: floating ? 0 : 10,
      }}
    >
      {notices.map((notice, index) => {
        const color = notice.type === "error"
          ? "#ef4444"
          : notice.type === "warning"
            ? "#d97706"
            : notice.type === "success"
              ? "#10b981"
              : "var(--accent)";
        return (
          <div
            key={notice.id}
            className="notice-shelf-item"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              minHeight: 46,
              marginBottom: index === notices.length - 1 ? 0 : 6,
              overflow: "hidden",
              borderRadius: 12,
              border: "1px solid color-mix(in srgb, var(--border) 70%, transparent)",
              background: "var(--bg)",
              color: "var(--text-muted)",
              width: "fit-content",
              maxWidth: "min(100%, 620px)",
              boxShadow: floating
                ? "0 1px 2px rgba(15,23,42,0.05), 0 10px 28px -14px rgba(15,23,42,0.24)"
                : "0 1px 2px rgba(15,23,42,0.04), 0 8px 24px -12px rgba(15,23,42,0.10)",
              fontSize: 13,
              lineHeight: 1.45,
              transformOrigin: "top center",
              animation: notice.exiting
                ? "notice-shelf-out 0.18s ease-in forwards"
                : "notice-shelf-in 0.18s ease-out both",
              padding: "0 12px",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: color,
                flexShrink: 0,
              }}
            />
            <span style={{ padding: "12px 0", minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {notice.message}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function ChatWindow({ session, newSessionCwd, onAgentEnd, onSessionCreated, onSessionForked, onQuotaChanged, modelsRefreshKey, chatInputRef, onBranchDataChange, onSystemPromptChange, onSessionStatsChange, onSessionStatsPanelOpen, onContextUsageChange, onOpenMembership, remainingChats, autoStartPrompt, onAutoStartPromptConsumed }: Props) {
  const [assistantDisplayName, setAssistantDisplayName] = useState("AI助手");
  const [welcomeGuidePhrases, setWelcomeGuidePhrases] = useState(TYPEWRITER_PHRASES);
  const [quotaModalMessage, setQuotaModalMessage] = useState<string | null>(null);
  const [dismissedSendError, setDismissedSendError] = useState<string | null>(null);
  const [followUpEligibleAssistantKey, setFollowUpEligibleAssistantKey] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const handleAgentStartForFollowUps = useCallback(() => {
    setFollowUpEligibleAssistantKey(null);
  }, []);

  const handleAssistantMessageEndForFollowUps = useCallback((message: AgentMessage) => {
    const key = assistantMessageKey(message);
    if (key) setFollowUpEligibleAssistantKey(key);
  }, []);

  const {
    loading, error, messages, entryIds, streamState,
    agentRunning, modelNames, modelList, modelThinkingLevels, modelThinkingLevelMaps, toolPreset, thinkingLevel,
    retryInfo, contextUsage, forkingEntryId,
    isCompacting, compactError, compactResult, sendError, displayModel: displayModelValue, sessionStats,
    slashCommands, slashCommandsLoading, notices, extensionWidgets, isAutoModelSelection,
    agentPhase,
    isNew,
    messagesEndRef, scrollContainerRef,
    lastUserMsgRef,
    handleSend, handleAbort, handleFork, handleNavigate, handleModelChange,
    handleCompact, handleSteer, handleFollowUp, handlePromptWithStreamingBehavior, handleAbortCompaction,
    handleBuiltinSlashCommand, loadSlashCommands,
    handleToolPresetChange, handleThinkingLevelChange,
  } = useAgentSession({
    session, newSessionCwd, onAgentEnd, onSessionCreated, onSessionForked, onQuotaChanged,
    modelsRefreshKey, onBranchDataChange, onSystemPromptChange,
    chatInputRef,
    onSessionStatsPanelOpen,
    onAgentStart: handleAgentStartForFollowUps,
    onAssistantMessageEnd: handleAssistantMessageEndForFollowUps,
  });

  useEffect(() => {
    fetch("/api/app-settings")
      .then((res) => res.ok ? res.json() : null)
      .then((data: { assistantDisplayName?: string; welcomeGuidePhrases?: string[] } | null) => {
        if (data?.assistantDisplayName) setAssistantDisplayName(data.assistantDisplayName);
        if (Array.isArray(data?.welcomeGuidePhrases) && data.welcomeGuidePhrases.length > 0) {
          setWelcomeGuidePhrases(data.welcomeGuidePhrases);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setFollowUpEligibleAssistantKey(null);
  }, [session?.id, newSessionCwd]);

  // Push session stats up to AppShell for the top bar.
  // Compare scalar fields to avoid loops from new object identity each render.
  const statsKey = sessionStats
    ? [
      sessionStats.sessionId,
      sessionStats.sessionFile ?? "",
      sessionStats.sessionName ?? "",
      sessionStats.userMessages,
      sessionStats.assistantMessages,
      sessionStats.toolCalls,
      sessionStats.toolResults,
      sessionStats.totalMessages,
      sessionStats.tokens.input,
      sessionStats.tokens.output,
      sessionStats.tokens.cacheRead,
      sessionStats.tokens.cacheWrite,
      sessionStats.tokens.total,
      sessionStats.cost ?? 0,
    ].join("|")
    : null;
  const sessionStatsRef = useRef(sessionStats);
  sessionStatsRef.current = sessionStats;
  useEffect(() => {
    onSessionStatsChange?.(sessionStatsRef.current);
  }, [statsKey, onSessionStatsChange]);
  useEffect(() => () => { onSessionStatsChange?.(null); }, [onSessionStatsChange]);

  // Push context usage up to AppShell as well.
  const ctxKey = contextUsage
    ? `${contextUsage.percent ?? "null"}|${contextUsage.contextWindow}|${contextUsage.tokens ?? "null"}`
    : null;
  const contextUsageRef = useRef(contextUsage);
  contextUsageRef.current = contextUsage;
  useEffect(() => {
    onContextUsageChange?.(contextUsageRef.current);
  }, [ctxKey, onContextUsageChange]);
  useEffect(() => () => { onContextUsageChange?.(null); }, [onContextUsageChange]);

  useEffect(() => {
    if (!sendError && !quotaModalMessage) setDismissedSendError(null);
  }, [sendError, quotaModalMessage]);

  const onDrop = useCallback((files: File[]) => {
    chatInputRef?.current?.addImages(files);
  }, [chatInputRef]);

  const { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop } = useDragDrop(onDrop);

  const visibleMessages = messages.filter((m) => m.role === "user" || m.role === "assistant");
  const visibleStreamingMessage = streamState.streamingMessage && streamState.streamingMessage.role !== "user"
    ? ({ ...streamState.streamingMessage, role: "assistant" } as Partial<AgentMessage>)
    : null;
  const messageRefs = useMessageRefs(visibleMessages.length);

  const isEmptyNew = isNew && messages.length === 0 && !streamState.isStreaming && !agentRunning;

  const availableThinkingLevels = displayModelValue
    ? (modelThinkingLevels[`${displayModelValue.provider}:${displayModelValue.modelId}`] ?? null)
    : null;

  const currentThinkingLevelMap = displayModelValue
    ? (modelThinkingLevelMaps[`${displayModelValue.provider}:${displayModelValue.modelId}`] ?? null)
    : null;
  const activeSendError = quotaModalMessage ?? sendError;
  const closeSendErrorModal = useCallback(() => {
    if (quotaModalMessage) {
      setQuotaModalMessage(null);
      setDismissedSendError(null);
      return;
    }
    if (activeSendError) setDismissedSendError(activeSendError);
  }, [activeSendError, quotaModalMessage]);

  const handleSendWithQuotaCheck = useCallback(async (message: string, images?: AttachedImage[]) => {
    setDismissedSendError(null);
    if (remainingChats !== undefined && remainingChats <= 0) {
      setQuotaModalMessage("剩余对话次数不足，请联系管理员增加次数或开通会员。");
      onQuotaChanged?.();
      return false;
    }
    setQuotaModalMessage(null);
    setFollowUpEligibleAssistantKey(null);
    return handleSend(message, images);
  }, [handleSend, onQuotaChanged, remainingChats]);

  const consumedAutoStartPromptRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoStartPrompt || consumedAutoStartPromptRef.current === autoStartPrompt.id) return;
    if (loading || agentRunning) return;
    consumedAutoStartPromptRef.current = autoStartPrompt.id;
    const id = window.setTimeout(async () => {
      const ok = await handleSendWithQuotaCheck(autoStartPrompt.prompt);
      if (ok !== false) onAutoStartPromptConsumed?.(autoStartPrompt.id);
      else consumedAutoStartPromptRef.current = null;
    }, 0);
    return () => window.clearTimeout(id);
  }, [agentRunning, autoStartPrompt, handleSendWithQuotaCheck, loading, onAutoStartPromptConsumed]);

  const handleGuideExample = useCallback((example: string) => {
    chatInputRef?.current?.insertText(example);
  }, [chatInputRef]);

  const handleFollowUpPrompt = useCallback((prompt: string) => {
    chatInputRef?.current?.insertIfEmpty(prompt);
  }, [chatInputRef]);

  const chatInputElement = (
    <ChatInput
      ref={chatInputRef}
      onSend={handleSendWithQuotaCheck}
      onAbort={handleAbort}
      onSteer={agentRunning ? handleSteer : undefined}
      onFollowUp={agentRunning ? handleFollowUp : undefined}
      onPromptWithStreamingBehavior={agentRunning ? handlePromptWithStreamingBehavior : undefined}
      isStreaming={agentRunning}
      model={displayModelValue}
      isAutoModelSelection={isAutoModelSelection}
      modelNames={modelNames}
      modelList={modelList}
      onModelChange={handleModelChange}
      onCompact={session || isNew ? handleCompact : undefined}
      onAbortCompaction={handleAbortCompaction}
      isCompacting={isCompacting}
      compactError={compactError}
      compactResult={compactResult}
      toolPreset={toolPreset}
      onToolPresetChange={session || isNew ? handleToolPresetChange : undefined}
      thinkingLevel={thinkingLevel}
      onThinkingLevelChange={session || isNew ? handleThinkingLevelChange : undefined}
      availableThinkingLevels={availableThinkingLevels}
      thinkingLevelMap={currentThinkingLevelMap}
      retryInfo={retryInfo}
      slashCommands={slashCommands}
      slashCommandsLoading={slashCommandsLoading}
      onLoadSlashCommands={loadSlashCommands}
      onBuiltinCommand={handleBuiltinSlashCommand}
    />
  );

  const aboveEditorWidgets = extensionWidgets.filter((widget) => widget.placement !== "belowEditor");
  const belowEditorWidgets = extensionWidgets.filter((widget) => widget.placement === "belowEditor");

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-text-muted">
        正在加载会话...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && (
        <div className="love-drop-zone pointer-events-none absolute inset-0 z-50 flex animate-[drop-zone-in_0.15s_ease_both] items-center justify-center backdrop-blur-[1px]">
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            {[0, 0.8, 1.6].map((delay) => (
              <div
                key={delay}
                className="love-drop-ripple absolute h-[720px] w-[720px] rounded-full border-[1.5px] border-solid animate-[drop-ripple_2.4s_ease-out_infinite_backwards]"
                style={{ transformOrigin: "center", animationDelay: `${delay}s` }}
              />
            ))}
          </div>
          <svg
            width="280" height="280" viewBox="0 0 140 140" fill="none" xmlns="http://www.w3.org/2000/svg"
            className="love-drop-illustration"
          >
            <rect x="28" y="44" width="84" height="60" rx="8" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.8"/>
            <path d="M36 100 L54 72 L68 88 L80 74 L104 100Z" fill="currentColor" fillOpacity="0.16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.4" strokeLinejoin="round"/>
            <circle cx="96" cy="58" r="8" fill="currentColor" fillOpacity="0.22" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.6"/>
            <g stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.4" strokeLinecap="round">
              <line x1="96" y1="46" x2="96" y2="43"/>
              <line x1="96" y1="70" x2="96" y2="73"/>
              <line x1="84" y1="58" x2="81" y2="58"/>
              <line x1="108" y1="58" x2="111" y2="58"/>
              <line x1="87.5" y1="49.5" x2="85.4" y2="47.4"/>
              <line x1="104.5" y1="66.5" x2="106.6" y2="68.6"/>
              <line x1="104.5" y1="49.5" x2="106.6" y2="47.4"/>
              <line x1="87.5" y1="66.5" x2="85.4" y2="68.6"/>
            </g>
          </svg>
        </div>
      )}

      {activeSendError && activeSendError !== dismissedSendError && (
        <div
          className="love-send-error-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-error-title"
          onClick={closeSendErrorModal}
        >
          <div
            className="love-send-error-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="love-send-error-head">
              <span className="love-send-error-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </span>
              <h2 id="send-error-title">发送失败</h2>
            </div>
            <div className="love-send-error-message">
              {activeSendError}
            </div>
            <div className="love-send-error-actions">
              {onOpenMembership && (
                <button
                  className="love-send-error-secondary"
                  onClick={() => {
                    closeSendErrorModal();
                    onOpenMembership();
                  }}
                >
                  查看会员套餐
                </button>
              )}
              <button
                className="love-send-error-primary"
                onClick={closeSendErrorModal}
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {isEmptyNew ? (
        <div className="new-chat-home love-coach-home flex flex-1 flex-col overflow-hidden">
          <div className="love-coach-home-scroll px-4">
            <div className="love-coach-home-inner">
              <section className="love-coach-hero">
                <div className="love-coach-hero-main">
                  <div className="love-coach-kicker">
                    <span aria-hidden="true">❤</span>
                    恋爱参谋默认在线
                  </div>
                  <h1>
                    看懂她的消息，
                    <span>知道怎么回。</span>
                  </h1>
                  <p>
                    发来女生原话、聊天截图或你们的相处背景，我会帮你判断她真正想表达什么、关系处在哪一步，以及下一句怎么说更稳。
                  </p>
                  <div className="love-coach-typewriter">
                    <Typewriter phrases={welcomeGuidePhrases} />
                  </div>
                  <div className="love-coach-primary-actions">
                    {GUIDE_ACTIONS.slice(0, 3).map((action) => (
                      <button key={action.title} type="button" onClick={() => handleGuideExample(action.prompt)}>
                        {action.title}
                      </button>
                    ))}
                  </div>
                </div>
                <aside className="love-coach-brief">
                  <div className="love-coach-brief-head">
                    <span>先给我这些</span>
                    <strong>越具体，判断越准</strong>
                  </div>
                  <div className="love-coach-materials">
                    {MATERIAL_HINTS.map((hint, index) => (
                      <div key={hint}>
                        <span>{index + 1}</span>
                        {hint}
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => handleGuideExample("我想让你帮我分析女生消息。我们现在的关系是：；她刚刚说：；我上一句说：；我的目标是：")}>
                    按模板开始
                  </button>
                </aside>
              </section>

              <section className="love-coach-flow">
                <div>
                  <span>怎么用</span>
                  <h2>不是让你背话术，而是帮你判断局势</h2>
                </div>
                <div className="love-coach-flow-steps">
                  {GUIDE_STEPS.map((step, index) => (
                    <div key={step.title}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{step.title}</strong>
                      <p>{step.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="love-coach-scenarios">
                {SCENARIO_PROMPTS.map((scenario) => (
                  <button key={scenario.title} type="button" onClick={() => handleGuideExample(scenario.prompt)}>
                    {scenario.title}
                  </button>
                ))}
              </section>
            </div>
          </div>
          <div className="love-coach-home-input">
            {chatInputElement}
          </div>
        </div>
      ) : (
      <>
      <div className="relative flex flex-1 overflow-hidden">
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 0,
            right: isMobile ? 0 : CHAT_MINIMAP_WIDTH,
            zIndex: 40,
            padding: `0 ${CHAT_COLUMN_PADDING}px`,
            pointerEvents: "none",
          }}
        >
          <div style={{ maxWidth: 820, margin: "0 auto" }}>
            <NoticeShelf notices={notices} floating align="right" />
          </div>
        </div>
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pt-4 [scrollbar-width:none]">
          <div className="love-chat-history-inner mx-auto max-w-[820px] px-4">

            {(() => {
              const toolResultsMap = new Map<string, import("@/lib/types").ToolResultMessage>();
              const hiddenToolIds = new Set<string>();
              for (const msg of messages) {
                for (const id of getHiddenSkillToolCallIds(msg)) hiddenToolIds.add(id);
                if (msg.role === "toolResult") {
                  const toolResult = msg as import("@/lib/types").ToolResultMessage;
                  if (!hiddenToolIds.has(toolResult.toolCallId)) toolResultsMap.set(toolResult.toolCallId, toolResult);
                }
              }
              let lastUserIdx = -1;
              for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].role === "user") { lastUserIdx = i; break; }
              }
              let refIdx = 0;
              const renderedUserKeys = new Set<string>();
              return messages.map((msg, idx) => {
                const userKey = userMessageKey(msg);
                if (userKey && renderedUserKeys.has(userKey)) return null;
                if (userKey) renderedUserKeys.add(userKey);
                const prevAssistantEntryId =
                  msg.role === "user" && idx > 0 && messages[idx - 1].role === "assistant"
                    ? entryIds[idx - 1]
                    : undefined;
                let previousUserMessage: string | null = null;
                if (msg.role === "assistant") {
                  for (let j = idx - 1; j >= 0; j--) {
                    const key = userMessageKey(messages[j]);
                    if (key) {
                      previousUserMessage = key;
                      break;
                    }
                  }
                }
                const isVisible = msg.role === "user" || msg.role === "assistant";
                const currentRefIdx = isVisible ? refIdx++ : -1;
                let showTimestamp = false;
                let isLastAssistantMessage = false;
                if (msg.role === "assistant") {
                  showTimestamp = true;
                  for (let j = idx + 1; j < messages.length; j++) {
                    const r = messages[j].role;
                    if (r === "user") break;
                    if (r === "assistant") { showTimestamp = false; break; }
                  }
                  // Hide on the currently-streaming tail (the streaming bubble owns the live timestamp)
                  if (showTimestamp && streamState.isStreaming && idx === messages.length - 1) {
                    showTimestamp = false;
                  }
                }
                const currentAssistantKey = assistantMessageKey(msg);
                isLastAssistantMessage = !!currentAssistantKey && currentAssistantKey === followUpEligibleAssistantKey;
                const view = (
                  <MessageView
                    key={idx}
                    message={msg}
                    toolResults={toolResultsMap}
                    modelNames={modelNames}
                    assistantDisplayName={assistantDisplayName}
                    entryId={entryIds[idx]}
                    onFork={agentRunning || isNew || (idx === 0 && msg.role === "user") ? undefined : handleFork}
                    forking={forkingEntryId === entryIds[idx]}
                    onNavigate={agentRunning ? undefined : handleNavigate}
                    prevAssistantEntryId={agentRunning ? undefined : prevAssistantEntryId}
                    onEditContent={(content) => chatInputRef?.current?.insertIfEmpty(content)}
                    onFollowUpPrompt={handleFollowUpPrompt}
                    previousUserMessage={previousUserMessage}
                    showFollowUpPrompts={isLastAssistantMessage && !streamState.isStreaming && !agentRunning}
                    showTimestamp={showTimestamp}
                  />
                );
                if (!isVisible) return view;
                return (
                  <div key={idx} ref={(el) => {
                    messageRefs.current[currentRefIdx] = el;
                    if (idx === lastUserIdx) { (lastUserMsgRef as { current: HTMLDivElement | null }).current = el; }
                  }}>
                    {view}
                  </div>
                );
              });
            })()}

            {streamState.isStreaming && visibleStreamingMessage && (
              <MessageView message={visibleStreamingMessage as AgentMessage} isStreaming modelNames={modelNames} assistantDisplayName={assistantDisplayName} onFollowUpPrompt={handleFollowUpPrompt} showFollowUpPrompts={false} />
            )}

            {agentRunning && !visibleStreamingMessage && (
              <div className="py-2 text-[13px] text-text-muted">
                <span className="animate-[pulse_1.5s_infinite]">{phaseLabel(agentPhase)}</span>
              </div>
            )}

            {agentRunning && (
              <div style={{ height: scrollContainerRef.current ? scrollContainerRef.current.clientHeight : "80vh" }} />
            )}

            <div ref={messagesEndRef} />
            </div>
          </div>
        {isMobile ? null : (
          <ChatMinimap
            messages={messages}
            streamingMessage={visibleStreamingMessage}
            scrollContainer={scrollContainerRef}
            messageRefs={messageRefs}
          />
        )}
      </div>

      <div className="relative">
        <div
          style={{
            padding: `0 ${CHAT_COLUMN_PADDING}px`,
            paddingRight: isMobile ? CHAT_COLUMN_PADDING : CHAT_INPUT_RIGHT_PADDING,
          }}
        >
          <div style={{ maxWidth: 820, margin: "0 auto" }}>
            <ExtensionWidgets widgets={belowEditorWidgets} />
          </div>
        </div>
        {chatInputElement}
      </div>
      </>
      )}
    </div>
  );
}
